import {
  formatError,
  login,
  runLogoutTimer,
  saveTokenInLocalStorage,
  signUp,
  sendPasswordReset,
  verifyResetCode,
  confirmPasswordResetWithCode,
  checkUserSignInMethods,
} from '../../services/AuthService';

import axios from 'axios';

import { getAuth } from 'firebase/auth';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { FacebookAuthProvider } from 'firebase/auth';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const apiUrl = process.env.REACT_APP_API_URL;
const apiVersion = process.env.REACT_APP_DEFAULT_API_VERSION;

export const SIGNUP_CONFIRMED_ACTION = '[signup action] confirmed signup';
export const SIGNUP_FAILED_ACTION = '[signup action] failed signup';
export const LOGIN_CONFIRMED_ACTION = '[login action] confirmed login';
export const COMPANY_SELECTED_ACTION = '[company action] company selected';
export const CREATE_NEW_COMPANY_ACTION = '[company action] create new company';
export const LOGIN_FAILED_ACTION = '[login action] failed login';
export const LOADING_TOGGLE_ACTION = '[Loading action] toggle loading';
export const LOGOUT_ACTION = '[Logout action] logout action';

export const PASSWORD_RESET_REQUEST = '[password reset] request sent';
export const PASSWORD_RESET_SUCCESS = '[password reset] success';
export const PASSWORD_RESET_FAILED = '[password reset] failed';

export const SET_ACCESS_TOKEN = 'SET_ACCESS_TOKEN';
export const SET_USER_DATA = 'SET_USER_DATA';
export const SET_SELECTED_COMPANY_ID = 'SET_SELECTED_COMPANY_ID';
export const SELECT_COMPANY = 'SELECT_COMPANY';
export const USER_AVATAR_UPDATED = 'USER_AVATAR_UPDATED';
export const USER_PROFILE_UPDATED = 'USER_PROFILE_UPDATED';
export const USER_COMPANY_REMOVED = 'USER_COMPANY_REMOVED';

export const setUserAvatar = (avatarUrl) => ({
  type: USER_AVATAR_UPDATED,
  payload: avatarUrl,
});

export const setUserProfile = ({ firstName, lastName, phoneNumber }) => ({
  type: USER_PROFILE_UPDATED,
  payload: { firstName, lastName, phoneNumber },
});

export const removeUserCompany = (companyId) => ({
  type: USER_COMPANY_REMOVED,
  payload: companyId,
});

// New action types for enhanced account linking
export const GOOGLE_ONLY_USER_DETECTED = 'GOOGLE_ONLY_USER_DETECTED';
export const ACCOUNT_METHODS_CHECKED = 'ACCOUNT_METHODS_CHECKED';
export const TRIGGER_GOOGLE_SIGNIN = 'TRIGGER_GOOGLE_SIGNIN';
export const CLEAR_AUTH_MESSAGES = 'CLEAR_AUTH_MESSAGES';

// Production-grade rate limiting and retry logic
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

async function withRetry(operation, maxRetries = MAX_RETRIES) {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Don't retry for certain error types
      const nonRetryableErrors = [
        'auth/user-not-found',
        'auth/invalid-email',
        'auth/weak-password',
        'auth/google-only-user',
        'auth/no-password-method',
        'auth/invalid-credential',
        'auth/wrong-password'
      ];

      if (nonRetryableErrors.includes(error.code)) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        throw error;
      }

      // Exponential backoff
      await delay(RETRY_DELAY * Math.pow(2, attempt - 1));
      console.log(`Retrying operation, attempt ${attempt + 1}/${maxRetries}`);
    }
  }

  throw lastError;
}

// Enhanced password reset action with smart account detection
export function sendPasswordResetAction(email) {
  return async (dispatch) => {
    try {
      dispatch(loadingToggleAction(true));

      // Enhanced: Check account type before attempting password reset
      const signInMethods = await withRetry(() => checkUserSignInMethods(email));

      if (signInMethods.isEmpty && !signInMethods.error) {
        throw {
          code: 'auth/user-not-found',
          message: 'Ingen användare hittades med den e-postadressen'
        };
      }

      if (signInMethods.hasGoogle && !signInMethods.hasPassword) {
        throw {
          code: 'auth/google-only-user',
          message: 'Detta konto använder Google för inloggning. Använd "Fortsätt med Google" istället.',
          canAddPassword: true,
          availableMethods: signInMethods.methods
        };
      }

      if (!signInMethods.hasPassword && signInMethods.methods.length > 0) {
        throw {
          code: 'auth/no-password-method',
          message: 'Detta konto har inget lösenord. Kontakta support för hjälp.',
          availableMethods: signInMethods.methods
        };
      }

      // Proceed with password reset for accounts that have passwords
      await withRetry(() => sendPasswordReset(email));

      dispatch({
        type: PASSWORD_RESET_SUCCESS,
        payload: {
          message: 'Återställningslänk skickad till din e-post',
          email: email
        }
      });

    } catch (error) {
      console.error('Password reset error:', error);

      let errorMessage = 'Ett fel inträffade vid skickande av återställningslänk';

      // Enhanced error handling based on our AuthService
      if (error.code) {
        switch (error.code) {
          case 'auth/user-not-found':
            errorMessage = 'Ingen användare hittades med den e-postadressen';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Ogiltig e-postadress';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'För många försök. Vänta en stund och försök igen';
            break;
          case 'auth/google-only-user':
            errorMessage = error.message;
            break;
          case 'auth/no-password-method':
            errorMessage = error.message;
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Nätverksfel. Kontrollera din internetanslutning och försök igen';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }

      dispatch({
        type: PASSWORD_RESET_FAILED,
        payload: {
          message: errorMessage,
          code: error.code,
          availableMethods: error.availableMethods || [],
          canAddPassword: error.canAddPassword || false
        }
      });

      // Only show formatError for actual Firebase errors, not our custom ones
      if (!error.code || (!error.code.includes('google-only-user') && !error.code.includes('no-password-method'))) {
        formatError(error);
      }

    } finally {
      dispatch(loadingToggleAction(false));
    }
  };
}

export function confirmPasswordResetAction(code, newPassword, history) {
  return async (dispatch) => {
    try {
      dispatch(loadingToggleAction(true));

      await withRetry(() => confirmPasswordResetWithCode(code, newPassword));

      dispatch({
        type: PASSWORD_RESET_SUCCESS,
        payload: { message: 'Lösenordet har återställts framgångsrikt' }
      });

      // Show success message before redirect
      setTimeout(() => {
        history.push('/login?message=password-reset-success');
      }, 1500);

    } catch (error) {
      console.error('Password reset confirmation error:', error);

      let errorMessage = 'Ett fel inträffade vid återställning av lösenord';

      if (error.code) {
        switch (error.code) {
          case 'auth/weak-password':
            errorMessage = 'Lösenordet är för svagt. Använd minst 8 tecken med stora och små bokstäver samt siffror';
            break;
          case 'auth/invalid-action-code':
            errorMessage = 'Återställningslänken är ogiltig eller felaktig';
            break;
          case 'auth/expired-action-code':
            errorMessage = 'Återställningslänken har gått ut. Begär en ny återställningslänk';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Nätverksfel. Kontrollera din internetanslutning och försök igen';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }

      dispatch({
        type: PASSWORD_RESET_FAILED,
        payload: { message: errorMessage }
      });

      formatError(error);
    } finally {
      dispatch(loadingToggleAction(false));
    }
  };
}

// Enhanced signup action with account linking detection
export function signupAction(firstName, lastName, phoneNumber, email, password, signupCode, history, redirectUrl) {
  return async (dispatch) => {
    let firebaseUser = null; // Track Firebase user for cleanup on failure

    try {
      dispatch(loadingToggleAction(true));

      // Input sanitization
      const sanitizedData = {
        firstName: firstName?.trim()?.substring(0, 50) || '',
        lastName: lastName?.trim()?.substring(0, 50) || '',
        phoneNumber: phoneNumber?.trim()?.substring(0, 20) || '',
        email: email?.trim()?.toLowerCase()?.substring(0, 254) || '',
        password: password?.substring(0, 255) || '',
        signupCode: signupCode?.trim()?.toLowerCase() || ''
      };

      // Check if account already exists before attempting signup
      const existingMethods = await withRetry(() => checkUserSignInMethods(sanitizedData.email));

      if (existingMethods.methods.length > 0 && !existingMethods.error) {
        let errorMessage = 'Ett konto med denna e-postadress finns redan.';

        if (existingMethods.hasGoogle && !existingMethods.hasPassword) {
          errorMessage += ' Logga in med "Fortsätt med Google" istället.';
        } else if (existingMethods.hasPassword) {
          errorMessage += ' Logga in istället.';
        }

        dispatch(signupFailedAction(errorMessage));
        return;
      }

      const response = await withRetry(() => signUp(sanitizedData.email, sanitizedData.password));
      firebaseUser = response.user; // Track for cleanup
      console.log("Signup response: ", response);
      saveTokenInLocalStorage(response);

      // Using axios make call to backend api to store user in database
      const backendResponse = await withRetry(() =>
        axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/users`, {
          email: response.user.email,
          googleId: response.user.uid,
          firstName: sanitizedData.firstName,
          lastName: sanitizedData.lastName,
          phoneNumber: sanitizedData.phoneNumber,
          signupCode: sanitizedData.signupCode,
        })
      );

      console.log("User added:", backendResponse);

      // Authenticate with the backend to get access token and set refresh token cookie
      // Without this, the browser may still hold a previous user's refresh token cookie,
      // causing the new user to see the old user's data
      const authData = await doLLSignIn(response, sanitizedData.signupCode);

      dispatch(confirmedSignupAction({
        email: response.user.email,
        googleUid: response.user.uid,
        uid: authData.userId,
        firstName: sanitizedData.firstName,
        lastName: sanitizedData.lastName,
      }));

      dispatch(setAccessToken(authData.accessToken));

      // Navigate to the appropriate page after signup
      if (redirectUrl === '/') {
        history.push('/select-company');
      } else {
        history.push(redirectUrl);
      }

    } catch (error) {
      console.log("Signup error: ", error);

      // Clean up Firebase user if it was created but backend registration failed
      // This prevents orphaned Firebase accounts (e.g. when signup code is invalid)
      if (firebaseUser) {
        try {
          await firebaseUser.delete();
          console.log('Firebase user deleted after failed signup');
        } catch (deleteError) {
          console.error('Failed to delete Firebase user after signup error:', deleteError);
        }
      }

      let errorMessage = 'Ett fel inträffade vid registrering';

      // Check for backend validation errors (signup code errors)
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'E-postadressen används redan. Logga in istället.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Lösenordet är för svagt. Använd minst 6 tecken.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Ogiltig e-postadress.';
            break;
          case 'auth/account-exists-different-credential':
            errorMessage = 'Ett konto med denna e-postadress finns redan med en annan inloggningsmetod.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Nätverksfel. Kontrollera din internetanslutning och försök igen';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }

      dispatch(signupFailedAction(errorMessage));
      formatError(error);
    } finally {
      dispatch(loadingToggleAction(false));
    }
  };
}

export const setAccessToken = (accessToken) => ({
  type: SET_ACCESS_TOKEN,
  payload: accessToken,
});

export const setUserData = (userData) => ({
  type: SET_USER_DATA,
  payload: userData,
});

export function logout(history) {
  // REVERTED to original behavior (owner decision 2026-07-28): auth flow
  // changes belong in their own session. The axios 401-interceptor
  // dispatches logout() WITHOUT a history; historically that crashed on
  // history.push AFTER clearing state — i.e. state cleared, no redirect,
  // route guards took over. Preserve exactly that outcome, minus the
  // TypeError: with no history we clear state and do NOTHING else. No
  // window.location redirects here, ever — a hard redirect on transient
  // boot-time 401s breaks the login → select-company flow.
  const redirect = () => {
    if (history && typeof history.push === 'function') {
      history.push('/login');
    }
  };
  return (dispatch) => {
    axios
      .post(
        `${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/logout`,
        {},
        { withCredentials: true }
      )
      .then((response) => {
        console.log('Logout response:', response);

        // Clear any local storage or state related to the user
        localStorage.removeItem('selectedCompany');
        localStorage.removeItem('userDetails');

        // Dispatch logout action to update Redux state
        dispatch({ type: LOGOUT_ACTION });

        // Redirect to login page after successful logout
        redirect();
      })
      .catch((error) => {
        console.log('Logout error:', error);

        // Handle errors if necessary - still clear local state
        localStorage.removeItem('selectedCompany');
        localStorage.removeItem('userDetails');
        dispatch({ type: LOGOUT_ACTION });
        redirect();
      });
  };
}

async function doLLSignIn(googleSignInData, signupCode = null) {
  try {
    const data = {
      name: googleSignInData.user.displayName,
      email: googleSignInData.user.email,
      userGoogleId: googleSignInData.user.uid,
      signupCode: signupCode,
      photoUrl: googleSignInData.user.photoURL || null
    };

    const response = await withRetry(() =>
      axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/google-signin`, data, {
        withCredentials: true,
        timeout: 10000 // 10 second timeout
      })
    );

    console.log("LedgerLoop sign in:", response.data);

    return {
      accessToken: response.data.accessToken,
      firstName: response.data.firstname,
      lastName: response.data.lastname,
      userId: response.data.userId,
      avatarUrl: response.data.avatarUrl || null
    };

  } catch (error) {
    console.error(`Error in doLLSignIn: ${error}`);
    throw error;
  }
}

// Separate function to fetch companies after authentication
async function fetchUserCompanies(googleId, accessToken) {
  try {
    const companiesResponse = await withRetry(() =>
      axios.get(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/users/companies?googleId=${googleId}`, {
        withCredentials: true,
        timeout: 10000,
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })
    );

    console.log("Companies response:", companiesResponse.data);

    // Convert companies array to object for easy access
    const companiesObject = {};
    // Handle both wrapped response { companies: [...] } and direct array [...]
    const companiesArray = companiesResponse.data.companies || companiesResponse.data;
    if (companiesArray && Array.isArray(companiesArray)) {
      companiesArray.forEach(company => {
        companiesObject[company.company_id] = {
          companyName: company.company_name,
          companyRegNo: company.company_reg_no || '',
          companyType: company.company_type || '',
          vatStatus: company.vat_status || '',
          registrationNumber: company.registration_number || '',
          roles: company.roles || []
        };
      });
    }

    return companiesObject;
  } catch (error) {
    console.error(`Error fetching companies: ${error}`);
    throw error;
  }
}

// Enhanced login action with better error handling and smart detection
export function loginAction(email, password, history, url) {
  return async (dispatch) => {
    try {
      dispatch(loadingToggleAction(true));

      // Input sanitization
      const sanitizedEmail = email?.trim()?.toLowerCase()?.substring(0, 254) || '';
      const sanitizedPassword = password?.substring(0, 255) || '';

      const response = await withRetry(() => login(sanitizedEmail, sanitizedPassword));

      console.log("Login response from google: ", response);

      const data = await doLLSignIn(response);

      console.log("Backend login response data: ", data);

      dispatch(loginConfirmedAction({
        email: response.user.email,
        googleUid: response.user.uid,
        uid: data.userId,
        firstName: data.firstName,
        lastName: data.lastName
      }));

      console.log("Login action dispatched");

      // Set access token in Redux state
      dispatch(setAccessToken(data.accessToken));

      console.log("Access token set in Redux state");

      // Fetch companies after setting access token, passing token directly
      const companies = await fetchUserCompanies(response.user.uid, data.accessToken);

      console.log("Fetched companies: ", companies);

      // Set user data with companies
      dispatch(setUserData({
        user: {
          user_id: data.userId,
          first_name: data.firstName,
          last_name: data.lastName,
          email: response.user.email,
          google_id: response.user.uid,
          phone_number: '', // Will be updated from user profile later
          photo_url: response.user.photoURL || null,
          avatar_url: data.avatarUrl || null
        },
        companies: companies
      }));

      if (url === '/') {
        history.push('/select-company');
      } else {
        history.push(url);
      }

    } catch (error) {
      console.error('Login action error:', error);

      let errorMessage = 'Ett fel inträffade vid inloggning';

      // Handle enhanced error messages from our AuthService
      if (error.code) {
        switch (error.code) {
          case 'auth/google-only-user':
            errorMessage = error.message;
            // Dispatch special action for Google-only users
            dispatch({
              type: GOOGLE_ONLY_USER_DETECTED,
              payload: {
                email: email?.trim()?.toLowerCase(),
                canTriggerGoogle: true,
                message: error.message
              }
            });
            break;
          case 'auth/invalid-credential':
          case 'auth/wrong-password':
            errorMessage = 'E-post eller lösenord är felaktigt';
            break;
          case 'auth/user-not-found':
            errorMessage = 'Ingen användare hittades med den e-postadressen. Omdirigerar till registrering...';
            dispatch(loginFailedAction(errorMessage));
            dispatch(loadingToggleAction(false));
            setTimeout(() => {
              dispatch(clearAuthMessages());
              history.push('/page-register');
            }, 2000);
            return;
          case 'auth/too-many-requests':
            errorMessage = 'För många inloggningsförsök. Vänta en stund och försök igen';
            break;
          case 'auth/user-disabled':
            errorMessage = 'Kontot har inaktiverats. Kontakta support';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Nätverksfel. Kontrollera din internetanslutning och försök igen';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }

      dispatch(loginFailedAction(errorMessage));

      // Only call formatError for non-Google-only errors to avoid double messaging
      if (error.code !== 'auth/google-only-user') {
        formatError(error);
      }

    } finally {
      dispatch(loadingToggleAction(false));
    }
  };
}

// Enhanced social login action with better error handling
export function socialLoginAction(provider, history, redirectUrl, signupCode = null) {
  console.log("Social login action: ", provider, redirectUrl);

  return async (dispatch) => {
    let firebaseUser = null; // Track the Firebase user for cleanup
    let isNewUser = false; // Track if this is a new user for cleanup on error

    try {
      dispatch(loadingToggleAction(true));

      // Initialize the provider based on which one was selected
      let authProvider;
      if (provider === 'google') {
        authProvider = new GoogleAuthProvider();
        // Add additional scopes if needed
        authProvider.addScope('email');
        authProvider.addScope('profile');
      } else {
        throw new Error(`Unsupported provider: ${provider}`);
      }

      // Get Firebase auth instance
      const auth = getAuth();

      // Sign in with popup
      const result = await withRetry(() => signInWithPopup(auth, authProvider));
      console.log("Social login result: ", result);

      // Store reference to Firebase user for potential cleanup
      firebaseUser = result.user;

      // Check if this is a new Firebase user
      isNewUser = result.additionalUserInfo?.isNewUser || false;
      console.log("Is new Firebase user:", isNewUser);

      // If new user and no signup code provided, block registration
      if (isNewUser && !signupCode) {
        console.log('New Firebase user without signup code - deleting and blocking');
        try {
          await firebaseUser.delete();
          console.log('Firebase user deleted successfully');
        } catch (deleteError) {
          console.error('Failed to delete Firebase user:', deleteError);
        }

        dispatch({
          type: LOGIN_FAILED_ACTION,
          payload: 'Du behöver en registreringskod för att skapa ett konto. Gå till registreringssidan och ange din kod.',
        });

        setTimeout(() => {
          dispatch(clearAuthMessages());
          history.push('/page-register');
        }, 2500);

        dispatch(loadingToggleAction(false));
        return;
      }

      const data = await doLLSignIn(result, signupCode);

      dispatch(loginConfirmedAction({
        email: result.user.email,
        googleUid: result.user.uid,
        uid: data.userId,
        firstName: data.firstName,
        lastName: data.lastName
      }));
      dispatch(setAccessToken(data.accessToken));

      // Fetch companies after setting access token, passing token directly
      const companies = await fetchUserCompanies(result.user.uid, data.accessToken);

      // Set user data with companies
      dispatch(setUserData({
        user: {
          user_id: data.userId,
          first_name: data.firstName,
          last_name: data.lastName,
          email: result.user.email,
          google_id: result.user.uid,
          phone_number: '', // Will be updated from user profile later
          photo_url: result.user.photoURL || null,
          avatar_url: data.avatarUrl || null
        },
        companies: companies
      }));

      if (redirectUrl === '/') {
        history.push('/select-company');
      } else {
        history.push(redirectUrl);
      }

    } catch (error) {
      console.error('Social login error:', error);

      // Clean up Firebase user if this was a new user and backend validation failed
      // This prevents orphaned Firebase accounts when signup code validation fails
      if (isNewUser && firebaseUser) {
        console.log('Backend validation failed for new user - cleaning up Firebase account');
        try {
          await firebaseUser.delete();
          console.log('Firebase user deleted successfully after backend failure');
        } catch (deleteError) {
          console.error('Failed to delete Firebase user after backend failure:', deleteError);
        }
      }

      // Handle specific errors
      let errorMessage = 'Social login misslyckades. Försök igen.';

      // Check if this is a signup code required error from backend (user exists in Firebase but not in our DB)
      const isSignupCodeError = error.response?.data?.error?.includes('Registreringskod') ||
                                error.response?.data?.message?.includes('Registreringskod');

      if (isSignupCodeError) {
        // User exists in Firebase but not in our backend - clean up Firebase account
        // This happens when user tries to login via Google but doesn't have an account in our system
        if (firebaseUser) {
          console.log('User not in backend DB - cleaning up Firebase account to prevent orphaned accounts');
          try {
            await firebaseUser.delete();
            console.log('Firebase user deleted successfully - user needs to register with signup code');
          } catch (deleteError) {
            console.error('Failed to delete Firebase user:', deleteError);
          }
        }

        errorMessage = 'Du behöver en registreringskod för att skapa ett konto. Gå till registreringssidan och ange din kod.';
        dispatch({
          type: LOGIN_FAILED_ACTION,
          payload: errorMessage,
        });
        setTimeout(() => {
          dispatch(clearAuthMessages());
          history.push('/page-register');
        }, 2500);
        return;
      }

      if (error.code) {
        switch (error.code) {
          case 'auth/popup-closed-by-user':
            errorMessage = 'Inloggning avbröts. Popup-fönstret stängdes.';
            break;
          case 'auth/popup-blocked':
            errorMessage = 'Popup blockerades av webbläsaren. Tillåt popup-fönster och försök igen.';
            break;
          case 'auth/account-exists-with-different-credential':
            errorMessage = 'Ett konto finns redan med samma e-postadress men olika inloggningsmetod.';
            break;
          case 'auth/cancelled-popup-request':
            // User cancelled - don't show error
            return;
          case 'auth/network-request-failed':
            errorMessage = 'Nätverksfel. Kontrollera din internetanslutning.';
            break;
          case 'auth/internal-error':
            errorMessage = 'Ett internt fel inträffade. Försök igen.';
            break;
          default:
            errorMessage = error.response?.data?.error || error.message || errorMessage;
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      dispatch({
        type: LOGIN_FAILED_ACTION,
        payload: errorMessage,
      });

    } finally {
      dispatch(loadingToggleAction(false));
    }
  };
}

// Company management actions
export function selectCompany(companyId, companyName, companyRegNumber, history, extras = {}) {
  return (dispatch, getState) => {
    // Pull the catalog-side fields from the companies map populated at login
    // when callers don't pass them in explicitly. Redux Thunk always supplies
    // getState — tests must mock it.
    const fromMap = getState()?.auth?.user?.companies?.[companyId] || {};
    const companyType = extras.companyType || fromMap.companyType || '';
    const vatStatus = extras.vatStatus || fromMap.vatStatus || '';
    const registrationNumber = extras.registrationNumber || fromMap.registrationNumber || '';

    localStorage.setItem('selectedCompany', JSON.stringify({
      companyId, companyName, companyRegNumber, companyType, vatStatus, registrationNumber,
    }));

    dispatch(companySelectedAction(
      companyId, companyName, companyRegNumber,
      { companyType, vatStatus, registrationNumber }
    ));
    history.push(`/${companyId}/dashboard`);
  };
}

export function setPreSelectedCompany(companyId, companyName, extras = {}) {
  return (dispatch, getState) => {
    const fromMap = getState()?.auth?.user?.companies?.[companyId] || {};
    const companyType = extras.companyType || fromMap.companyType || '';
    const vatStatus = extras.vatStatus || fromMap.vatStatus || '';
    const registrationNumber = extras.registrationNumber || fromMap.registrationNumber || '';
    dispatch(companySelectedAction(
      companyId, companyName, undefined,
      { companyType, vatStatus, registrationNumber }
    ));
  };
}

export function createNewCompany(history) {
  return (dispatch) => {
    dispatch(createNewCompanyAction());
    history.push('/create-company');
  };
}

// Basic action creators
export function loginFailedAction(data) {
  return {
    type: LOGIN_FAILED_ACTION,
    payload: data,
  };
}

export function loginConfirmedAction(data) {
  console.log("Login confirmed action: ", data);
  return {
    type: LOGIN_CONFIRMED_ACTION,
    payload: {
      data
    },
  };
}

export function confirmedSignupAction(payload) {
  return {
    type: SIGNUP_CONFIRMED_ACTION,
    payload: {
      data: payload,
    },
  };
}

export function signupFailedAction(message) {
  return {
    type: SIGNUP_FAILED_ACTION,
    payload: message,
  };
}

export function loadingToggleAction(status) {
  return {
    type: LOADING_TOGGLE_ACTION,
    payload: status,
  };
}

export function companySelectedAction(companyId, companyName, companyRegNumber, extras = {}) {
  return {
    type: COMPANY_SELECTED_ACTION,
    payload: {
      companyId,
      companyName,
      companyRegNumber,
      companyType: extras.companyType || '',
      vatStatus: extras.vatStatus || '',
      registrationNumber: extras.registrationNumber || '',
    },
  };
}

export function createNewCompanyAction(newCompany) {
  return {
    type: CREATE_NEW_COMPANY_ACTION,
    payload: newCompany,
  };
}

// Enhanced action creators for account linking
export function triggerGoogleSignInAction() {
  return {
    type: TRIGGER_GOOGLE_SIGNIN,
    payload: { shouldTriggerGoogle: true }
  };
}

export function checkAccountMethodsAction(email) {
  return async (dispatch) => {
    try {
      const methods = await withRetry(() => checkUserSignInMethods(email));

      dispatch({
        type: ACCOUNT_METHODS_CHECKED,
        payload: {
          email,
          methods: methods.methods,
          hasPassword: methods.hasPassword,
          hasGoogle: methods.hasGoogle
        }
      });

      return methods;
    } catch (error) {
      console.error('Error checking account methods:', error);
      return { hasPassword: false, hasGoogle: false, methods: [], error: true };
    }
  };
}

export function clearAuthMessages() {
  return {
    type: CLEAR_AUTH_MESSAGES,
    payload: {}
  };
}



// import {
//   formatError,
//   login,
//   runLogoutTimer,
//   saveTokenInLocalStorage,
//   signUp,
//   sendPasswordReset,
//   verifyResetCode,
//   confirmPasswordResetWithCode,
//   checkUserSignInMethods,
// } from '../../services/AuthService';

// import axios from 'axios';

// import { getAuth } from 'firebase/auth';
// import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
// import { FacebookAuthProvider } from 'firebase/auth';

// const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
// const apiUrl = process.env.REACT_APP_API_URL;
// const apiVersion = process.env.REACT_APP_DEFAULT_API_VERSION;

// export const SIGNUP_CONFIRMED_ACTION = '[signup action] confirmed signup';
// export const SIGNUP_FAILED_ACTION = '[signup action] failed signup';
// export const LOGIN_CONFIRMED_ACTION = '[login action] confirmed login';
// export const COMPANY_SELECTED_ACTION = '[company action] company selected';
// export const CREATE_NEW_COMPANY_ACTION = '[company action] create new company';
// export const LOGIN_FAILED_ACTION = '[login action] failed login';
// export const LOADING_TOGGLE_ACTION = '[Loading action] toggle loading';
// export const LOGOUT_ACTION = '[Logout action] logout action';

// export const PASSWORD_RESET_REQUEST = '[password reset] request sent';
// export const PASSWORD_RESET_SUCCESS = '[password reset] success';
// export const PASSWORD_RESET_FAILED = '[password reset] failed';

// export const SET_ACCESS_TOKEN = 'SET_ACCESS_TOKEN';
// export const SET_USER_DATA = 'SET_USER_DATA';
// export const SET_SELECTED_COMPANY_ID = 'SET_SELECTED_COMPANY_ID';

// // Enhanced password reset action with smart account detection
// export function sendPasswordResetAction(email) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       // Enhanced: Check account type before attempting password reset
//       const signInMethods = await checkUserSignInMethods(email);

//       if (signInMethods.isEmpty && !signInMethods.error) {
//         throw {
//           code: 'auth/user-not-found',
//           message: 'Ingen användare hittades med den e-postadressen'
//         };
//       }

//       if (signInMethods.hasGoogle && !signInMethods.hasPassword) {
//         throw {
//           code: 'auth/google-only-user',
//           message: 'Detta konto använder Google för inloggning. Använd "Fortsätt med Google" istället.',
//           canAddPassword: true,
//           availableMethods: signInMethods.methods
//         };
//       }

//       if (!signInMethods.hasPassword && signInMethods.methods.length > 0) {
//         throw {
//           code: 'auth/no-password-method',
//           message: 'Detta konto har inget lösenord. Kontakta support för hjälp.',
//           availableMethods: signInMethods.methods
//         };
//       }

//       // Proceed with password reset for accounts that have passwords
//       await sendPasswordReset(email);

//       dispatch({
//         type: PASSWORD_RESET_SUCCESS,
//         payload: {
//           message: 'Återställningslänk skickad till din e-post',
//           email: email
//         }
//       });

//     } catch (error) {
//       console.error('Password reset error:', error);

//       let errorMessage = 'Ett fel inträffade vid skickande av återställningslänk';

//       // Enhanced error handling based on our AuthService
//       if (error.code) {
//         switch (error.code) {
//           case 'auth/user-not-found':
//             errorMessage = 'Ingen användare hittades med den e-postadressen';
//             break;
//           case 'auth/invalid-email':
//             errorMessage = 'Ogiltig e-postadress';
//             break;
//           case 'auth/too-many-requests':
//             errorMessage = 'För många försök. Vänta en stund och försök igen';
//             break;
//           case 'auth/google-only-user':
//             errorMessage = error.message;
//             break;
//           case 'auth/no-password-method':
//             errorMessage = error.message;
//             break;
//           default:
//             errorMessage = error.message || errorMessage;
//         }
//       }

//       dispatch({
//         type: PASSWORD_RESET_FAILED,
//         payload: {
//           message: errorMessage,
//           code: error.code,
//           availableMethods: error.availableMethods || [],
//           canAddPassword: error.canAddPassword || false
//         }
//       });

//       // Only show formatError for actual Firebase errors, not our custom ones
//       if (!error.code || !error.code.startsWith('auth/google-only-user') && !error.code.startsWith('auth/no-password-method')) {
//         formatError(error);
//       }

//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// export function confirmPasswordResetAction(code, newPassword, history) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       await confirmPasswordResetWithCode(code, newPassword);

//       dispatch({
//         type: PASSWORD_RESET_SUCCESS,
//         payload: { message: 'Lösenordet har återställts framgångsrikt' }
//       });

//       // Show success message before redirect
//       setTimeout(() => {
//         history.push('/login?message=password-reset-success');
//       }, 1500);

//     } catch (error) {
//       console.error('Password reset confirmation error:', error);

//       let errorMessage = 'Ett fel inträffade vid återställning av lösenord';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/weak-password':
//             errorMessage = 'Lösenordet är för svagt. Använd minst 8 tecken med stora och små bokstäver samt siffror';
//             break;
//           case 'auth/invalid-action-code':
//             errorMessage = 'Återställningslänken är ogiltig eller felaktig';
//             break;
//           case 'auth/expired-action-code':
//             errorMessage = 'Återställningslänken har gått ut. Begär en ny återställningslänk';
//             break;
//           default:
//             errorMessage = error.message || errorMessage;
//         }
//       }

//       dispatch({
//         type: PASSWORD_RESET_FAILED,
//         payload: { message: errorMessage }
//       });

//       formatError(error);
//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// // Enhanced signup action with account linking detection
// export function signupAction(firstName, lastName, phoneNumber, email, password, history, redirectUrl) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       // Check if account already exists before attempting signup
//       const existingMethods = await checkUserSignInMethods(email);

//       if (existingMethods.methods.length > 0 && !existingMethods.error) {
//         let errorMessage = 'Ett konto med denna e-postadress finns redan.';

//         if (existingMethods.hasGoogle && !existingMethods.hasPassword) {
//           errorMessage += ' Logga in med "Fortsätt med Google" istället.';
//         } else if (existingMethods.hasPassword) {
//           errorMessage += ' Logga in istället.';
//         }

//         dispatch(signupFailedAction(errorMessage));
//         return;
//       }

//       const response = await signUp(email, password);
//       console.log("Signup response: ", response);
//       saveTokenInLocalStorage(response);

//       // Using axios make call to backend api to store user in database
//       const backendResponse = await axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/users`, {
//         email: response.user.email,
//         googleId: response.user.uid,
//         firstName: firstName,
//         lastName: lastName,
//         phoneNumber: phoneNumber,
//       });

//       console.log("User added:", backendResponse);

//       const llUid = backendResponse.data.result.userId;

//       dispatch(confirmedSignupAction({
//         email: response.user.email,
//         googleUid: response.user.uid,
//         uid: llUid,
//         firstName: firstName,
//         lastName: lastName,
//       }));

//       dispatch(setAccessToken(backendResponse.data.accessToken));

//       // Navigate to the appropriate page after signup
//       if (redirectUrl === '/') {
//         history.push('/auth/select-company');
//       } else {
//         history.push(redirectUrl);
//       }

//     } catch (error) {
//       console.log("Signup error: ", error);

//       let errorMessage = 'Ett fel inträffade vid registrering';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/email-already-in-use':
//             errorMessage = 'E-postadressen används redan. Logga in istället.';
//             break;
//           case 'auth/weak-password':
//             errorMessage = 'Lösenordet är för svagt. Använd minst 6 tecken.';
//             break;
//           case 'auth/invalid-email':
//             errorMessage = 'Ogiltig e-postadress.';
//             break;
//           case 'auth/account-exists-different-credential':
//             errorMessage = 'Ett konto med denna e-postadress finns redan med en annan inloggningsmetod.';
//             break;
//           default:
//             errorMessage = error.message || errorMessage;
//         }
//       }

//       dispatch(signupFailedAction(errorMessage));
//       formatError(error);
//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// export const setAccessToken = (accessToken) => ({
//   type: SET_ACCESS_TOKEN,
//   payload: accessToken,
// });

// export const setUserData = (userData) => ({
//   type: SET_USER_DATA,
//   payload: userData,
// });

// export function logout(history) {
//   return (dispatch) => {
//     axios
//       .post(
//         `${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/logout`,
//         {},
//         { withCredentials: true }
//       )
//       .then((response) => {
//         console.log('Logout response:', response);

//         // Clear any local storage or state related to the user
//         localStorage.removeItem('selectedCompany');
//         localStorage.removeItem('userDetails');

//         // Dispatch logout action to update Redux state
//         dispatch({ type: LOGOUT_ACTION });

//         // Redirect to login page after successful logout
//         history.push('/login');
//       })
//       .catch((error) => {
//         console.log('Logout error:', error);

//         // Handle errors if necessary - still clear local state
//         localStorage.removeItem('selectedCompany');
//         localStorage.removeItem('userDetails');
//         dispatch({ type: LOGOUT_ACTION });
//         history.push('/login');
//       });
//   };
// }

// async function doLLSignIn(googelSignInData) {
//   try {
//     const data = {
//       name: googelSignInData.user.displayName,
//       email: googelSignInData.user.email,
//       userGoogleId: googelSignInData.user.uid
//     };

//     const response = await axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/google-signin`, data, { withCredentials: true });
//     console.log("LedgerLoop sign in:", response.data);

//     return {
//       accessToken: response.data.accessToken,
//       firstName: response.data.firstname,
//       lastName: response.data.lastname,
//       userId: response.data.userId,
//     };

//   } catch (error) {
//     console.error(`Error: ${error}`);
//     throw error;
//   }
// }

// // Enhanced login action with better error handling
// export function loginAction(email, password, history, url) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       const response = await login(email, password);
//       console.log("Login response: ", response);

//       const data = await doLLSignIn(response);
//       console.log("Data: ", data);

//       dispatch(loginConfirmedAction({
//         email: response.user.email,
//         googleUid: response.user.uid,
//         uid: data.userId,
//         firstName: data.firstName,
//         lastName: data.lastName
//       }));
//       dispatch(setAccessToken(data.accessToken));

//       if (url === '/') {
//         history.push('/select-company');
//       } else {
//         history.push(url);
//       }

//     } catch (error) {
//       console.error('Login action error:', error);

//       let errorMessage = 'Ett fel inträffade vid inloggning';

//       // Handle enhanced error messages from our AuthService
//       if (error.code) {
//         switch (error.code) {
//           case 'auth/google-only-user':
//             errorMessage = error.message;
//             // Dispatch special action for Google-only users
//             dispatch({
//               type: 'GOOGLE_ONLY_USER_DETECTED',
//               payload: { email, canTriggerGoogle: true }
//             });
//             break;
//           case 'auth/invalid-credential':
//           case 'auth/wrong-password':
//             errorMessage = 'E-post eller lösenord är felaktigt';
//             break;
//           case 'auth/user-not-found':
//             errorMessage = 'Ingen användare hittades med den e-postadressen';
//             break;
//           case 'auth/too-many-requests':
//             errorMessage = 'För många inloggningsförsök. Vänta en stund och försök igen';
//             break;
//           case 'auth/user-disabled':
//             errorMessage = 'Kontot har inaktiverats. Kontakta support';
//             break;
//           default:
//             errorMessage = error.message || errorMessage;
//         }
//       }

//       dispatch(loginFailedAction(errorMessage));

//       // Only call formatError for non-Google-only errors to avoid double messaging
//       if (error.code !== 'auth/google-only-user') {
//         formatError(error);
//       }

//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// // Enhanced social login action
// export function socialLoginAction(provider, history, redirectUrl) {
//   console.log("Social login action: ", provider, redirectUrl);

//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       // Initialize the provider based on which one was selected
//       let authProvider;
//       authProvider = new GoogleAuthProvider();

//       // Get Firebase auth instance
//       const auth = getAuth();

//       // Sign in with popup
//       const result = await signInWithPopup(auth, authProvider);
//       console.log("Social login result: ", result);

//       const data = await doLLSignIn(result);

//       dispatch(loginConfirmedAction({
//         email: result.user.email,
//         googleUid: result.user.uid,
//         uid: data.userId,
//         firstName: data.firstName,
//         lastName: data.lastName
//       }));
//       dispatch(setAccessToken(data.accessToken));

//       if (redirectUrl === '/') {
//         history.push('/select-company');
//       } else {
//         history.push(redirectUrl);
//       }

//     } catch (error) {
//       console.error('Social login error:', error);

//       // Handle specific errors
//       let errorMessage = 'Social login misslyckades. Försök igen.';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/popup-closed-by-user':
//             errorMessage = 'Inloggning avbröts. Popup-fönstret stängdes.';
//             break;
//           case 'auth/popup-blocked':
//             errorMessage = 'Popup blockerades av webbläsaren. Tillåt popup-fönster och försök igen.';
//             break;
//           case 'auth/account-exists-with-different-credential':
//             errorMessage = 'Ett konto finns redan med samma e-postadress men olika inloggningsmetod.';
//             break;
//           case 'auth/cancelled-popup-request':
//             // User cancelled - don't show error
//             return;
//           case 'auth/network-request-failed':
//             errorMessage = 'Nätverksfel. Kontrollera din internetanslutning.';
//             break;
//           default:
//             errorMessage = error.message || errorMessage;
//         }
//       }

//       dispatch({
//         type: LOGIN_FAILED_ACTION,
//         payload: errorMessage,
//       });

//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// export function selectCompany(companyId, companyName, companyRegNumber, history) {
//   return (dispatch) => {
//     localStorage.setItem('selectedCompany', JSON.stringify({ companyId, companyName, companyRegNumber }));

//     dispatch(companySelectedAction(companyId, companyName, companyRegNumber));
//     history.push(`/${companyId}/dashboard`);
//   };
// }

// export function setPreSelectedCompany(companyId, companyName) {
//   return (dispatch) => {
//     dispatch(companySelectedAction(companyId, companyName));
//   };
// }

// export function createNewCompany(history) {
//   return (dispatch) => {
//     dispatch(createNewCompanyAction());
//     history.push('/create-company');
//   };
// }

// export function loginFailedAction(data) {
//   return {
//     type: LOGIN_FAILED_ACTION,
//     payload: data,
//   };
// }

// export function loginConfirmedAction(data) {
//   console.log("Login confirmed action: ", data);
//   return {
//     type: LOGIN_CONFIRMED_ACTION,
//     payload: {
//       data
//     },
//   };
// }

// export function confirmedSignupAction(payload) {
//   return {
//     type: SIGNUP_CONFIRMED_ACTION,
//     payload: {
//       data: payload,
//     },
//   };
// }

// export function signupFailedAction(message) {
//   return {
//     type: SIGNUP_FAILED_ACTION,
//     payload: message,
//   };
// }

// export function loadingToggleAction(status) {
//   return {
//     type: LOADING_TOGGLE_ACTION,
//     payload: status,
//   };
// }

// export function companySelectedAction(companyId, companyName, companyRegNumber) {
//   return {
//     type: COMPANY_SELECTED_ACTION,
//     payload: { companyId, companyName, companyRegNumber },
//   };
// }

// export function createNewCompanyAction(newCompany) {
//   return {
//     type: CREATE_NEW_COMPANY_ACTION,
//     payload: newCompany,
//   };
// }

// // New action to handle account linking scenarios
// export function triggerGoogleSignInAction() {
//   return {
//     type: 'TRIGGER_GOOGLE_SIGNIN',
//     payload: { shouldTriggerGoogle: true }
//   };
// }

// // New action for handling account method detection
// export function checkAccountMethodsAction(email) {
//   return async (dispatch) => {
//     try {
//       const methods = await checkUserSignInMethods(email);

//       dispatch({
//         type: 'ACCOUNT_METHODS_CHECKED',
//         payload: {
//           email,
//           methods: methods.methods,
//           hasPassword: methods.hasPassword,
//           hasGoogle: methods.hasGoogle
//         }
//       });

//       return methods;
//     } catch (error) {
//       console.error('Error checking account methods:', error);
//       return { hasPassword: false, hasGoogle: false, methods: [], error: true };
//     }
//   };
// }



// import {
//   formatError,
//   login,
//   runLogoutTimer,
//   saveTokenInLocalStorage,
//   signUp,
//   sendPasswordReset,
//   verifyResetCode,
//   confirmPasswordResetWithCode,
// } from '../../services/AuthService';

// import axios from 'axios';

// import { getAuth } from 'firebase/auth';
// import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
// import { FacebookAuthProvider } from 'firebase/auth';

// const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
// const apiUrl = process.env.REACT_APP_API_URL;
// const apiVersion = process.env.REACT_APP_DEFAULT_API_VERSION;


// export const SIGNUP_CONFIRMED_ACTION = '[signup action] confirmed signup';
// export const SIGNUP_FAILED_ACTION = '[signup action] failed signup';
// export const LOGIN_CONFIRMED_ACTION = '[login action] confirmed login';
// export const COMPANY_SELECTED_ACTION = '[company action] company selected';
// export const CREATE_NEW_COMPANY_ACTION = '[company action] create new company';
// export const LOGIN_FAILED_ACTION = '[login action] failed login';
// export const LOADING_TOGGLE_ACTION = '[Loading action] toggle loading';
// export const LOGOUT_ACTION = '[Logout action] logout action';

// export const PASSWORD_RESET_REQUEST = '[password reset] request sent';
// export const PASSWORD_RESET_SUCCESS = '[password reset] success';
// export const PASSWORD_RESET_FAILED = '[password reset] failed';

// export const SET_ACCESS_TOKEN = 'SET_ACCESS_TOKEN';
// export const SET_USER_DATA = 'SET_USER_DATA';
// export const SET_SELECTED_COMPANY_ID = 'SET_SELECTED_COMPANY_ID';


// // Add password reset actions
// export function sendPasswordResetAction(email) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       await sendPasswordReset(email);

//       dispatch({
//         type: PASSWORD_RESET_SUCCESS,
//         payload: { message: 'Återställningslänk skickad till din e-post' }
//       });

//       // Show success message
//       // You can use swal here or handle in component

//     } catch (error) {
//       console.error('Password reset error:', error);

//       let errorMessage = 'Ett fel inträffade vid skickande av återställningslänk';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/user-not-found':
//             errorMessage = 'Ingen användare hittades med den e-postadressen';
//             break;
//           case 'auth/invalid-email':
//             errorMessage = 'Ogiltig e-postadress';
//             break;
//           case 'auth/too-many-requests':
//             errorMessage = 'För många försök. Vänta en stund och försök igen';
//             break;
//           default:
//             errorMessage = error.message;
//         }
//       }

//       dispatch({
//         type: PASSWORD_RESET_FAILED,
//         payload: { message: errorMessage }
//       });

//       formatError(error);
//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// export function confirmPasswordResetAction(code, newPassword, history) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       await confirmPasswordResetWithCode(code, newPassword);

//       dispatch({
//         type: PASSWORD_RESET_SUCCESS,
//         payload: { message: 'Lösenordet har återställts framgångsrikt' }
//       });

//       // Redirect to login
//       history.push('/login');

//     } catch (error) {
//       console.error('Password reset confirmation error:', error);

//       let errorMessage = 'Ett fel inträffade vid återställning av lösenord';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/weak-password':
//             errorMessage = 'Lösenordet är för svagt. Använd minst 6 tecken';
//             break;
//           case 'auth/invalid-action-code':
//           case 'auth/expired-action-code':
//             errorMessage = 'Återställningslänken är ogiltig eller har löpt ut';
//             break;
//           default:
//             errorMessage = error.message;
//         }
//       }

//       dispatch({
//         type: PASSWORD_RESET_FAILED,
//         payload: { message: errorMessage }
//       });

//       formatError(error);
//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }


// export function signupAction(firstName, lastName, phoneNumber, email, password, history, redirectUrl) {
//   return (dispatch) => {
//     signUp(email, password)
//       .then((response) => {
//         console.log("Signup response: ", response);
//         saveTokenInLocalStorage(response);

//         //Using axios make call to backend api to store user in database
//         axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/users`, {
//           email: response.user.email,
//           googleId: response.user.uid,
//           firstName: firstName,
//           lastName: lastName,
//           phoneNumber: phoneNumber,
//         })
//           .then((backendResponse) => {
//             console.log("User added:", backendResponse);

//             const llUid = backendResponse.data.result.userId;

//             /*runLogoutTimer(
//               dispatch,
//               response._tokenResponse.expiresIn * 1000,
//               history,
//             );*/

//             console.log("Dispatching confirmedSignupAction");
//             console.log("Redirect URL: ", redirectUrl);
//             console.log("The response: ", response);

//             dispatch(confirmedSignupAction(
//               {
//                 email: response.user.email,
//                 googleUid: response.user.uid,
//                 uid: llUid,
//                 firstName: firstName,
//                 lastName: lastName,
//               }
//             ));

//             dispatch(setAccessToken(backendResponse.data.accessToken));


//             // Navigate to the appropriate page after signup
//             if (redirectUrl === '/') {
//               history.push('/auth/select-company');
//             } else {
//               history.push(redirectUrl);
//             }
//           })
//           .catch((error) => {
//             console.log("Backend API error:", error);
//             // Handle backend API error
//             const errorMessage = formatError(error.response);
//             dispatch(signupFailedAction(errorMessage));
//           });
//       })
//       .catch((error) => {
//         console.log("Signup error: ", error);
//         const errorMessage = formatError(error.response);
//         dispatch(signupFailedAction(errorMessage));
//       });
//   };
// }


// export const setAccessToken = (accessToken) => ({
//   type: SET_ACCESS_TOKEN,
//   payload: accessToken,
// });

// export const setUserData = (userData) => ({
//   type: SET_USER_DATA,
//   payload: userData,
// });


// export function logout(history) {
//   return (dispatch) => {
//     axios
//       .post(
//         `${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/logout`,
//         {},
//         { withCredentials: true }
//       )
//       .then((response) => {
//         console.log('Logout response:', response);

//         // Clear any local storage or state related to the user
//         localStorage.removeItem('selectedCompany');

//         // Dispatch logout action to update Redux state
//         dispatch({ type: LOGOUT_ACTION });

//         // Redirect to login page after successful logout
//         history.push('/login');
//       })
//       .catch((error) => {
//         console.log('Logout error:', error);

//         // Handle errors if necessary
//         // For example, you might still want to redirect even if logout failed
//         dispatch({ type: LOGOUT_ACTION });
//         history.push('/login');
//       });
//   };
// }


// async function doLLSignIn(googelSignInData) {
//   try {
//     const data = {
//       name: googelSignInData.user.displayName,
//       email: googelSignInData.user.email,
//       userGoogleId: googelSignInData.user.uid
//     };

//     const response = await axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/google-signin`, data, { withCredentials: true });
//     console.log("LedgerLop sign in:", response.data);

//     return {
//       accessToken: response.data.accessToken,
//       firstName: response.data.firstname,
//       lastName: response.data.lastname,
//       userId: response.data.userId,
//     };

//   } catch (error) {
//     console.error(`Error: ${error}`);
//   }
// }

// export function loginAction(email, password, history, url) {
//   return (dispatch) => {
//     login(email, password)
//       .then(async (response) => {

//         console.log("Login response: ", response);

//         const data = await doLLSignIn(response);

//         console.log("Data: ", data);

//         dispatch(loginConfirmedAction({
//           email: response.user.email,
//           googleUid: response.user.uid,
//           uid: data.userId,
//           firstName: data.firstName,
//           lastName: data.lastName
//         }));
//         dispatch(setAccessToken(data.accessToken));

//         if (url === '/') {
//           history.push('/select-company'); //Used to be /dashboard
//         } else {
//           history.push(url);
//         }

//       })
//       .catch((error) => {
//         const errorMessage = error.message;
//         formatError(error);
//         dispatch(loginFailedAction(errorMessage));
//       });
//   };
// }

// export function socialLoginAction(provider, history, redirectUrl) {

//   console.log("Social login action: ", provider, redirectUrl);

//   return async (dispatch) => {
//     try {
//       // Initialize the provider based on which one was selected
//       let authProvider;

//       authProvider = new GoogleAuthProvider();

//       // Get Firebase auth instance
//       const auth = getAuth();

//       // Sign in with popup
//       const result = await signInWithPopup(auth, authProvider);

//       console.log("Social login result: ", result);

//       const data = await doLLSignIn(result);

//       dispatch(loginConfirmedAction({
//         email: result.user.email,
//         googleUid: result.user.uid,
//         uid: data.userId,
//         firstName: data.firstName,
//         lastName: data.lastName
//       }));
//       dispatch(setAccessToken(data.accessToken));

//       if (redirectUrl === '/') {
//         history.push('/select-company'); //Used to be /dashboard
//       } else {
//         history.push(redirectUrl);
//       }

//       // Get the user token
//       /*const token = await result.user.getIdToken();

//       // Optionally get additional user info
//       const user = {
//         email: result.user.email,
//         displayName: result.user.displayName,
//         photoURL: result.user.photoURL,
//         uid: result.user.uid,
//         token,
//       };

//       // Save to local storage or state management
//       localStorage.setItem('userDetails', JSON.stringify(user));

//       // Dispatch login confirmed action
//       dispatch({
//         type: LOGIN_CONFIRMED_ACTION,
//         payload: user,
//       });

//       // Redirect user
//       history.push(redirectUrl);
//       */
//     } catch (error) {
//       console.error('Social login error:', error);

//       // Handle specific errors
//       let errorMessage = 'Social login failed. Please try again.';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/popup-closed-by-user':
//             errorMessage = 'Login canceled. The popup was closed.';
//             break;
//           case 'auth/account-exists-with-different-credential':
//             errorMessage = 'An account already exists with the same email address but different sign-in credentials.';
//             break;
//           default:
//             errorMessage = error.message;
//         }
//       }

//       dispatch({
//         type: LOGIN_FAILED_ACTION,
//         payload: errorMessage,
//       });
//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// export function selectCompany(companyId, companyName, companyRegNumber, history) {
//   return (dispatch) => {
//     localStorage.setItem('selectedCompany', JSON.stringify({ companyId, companyName, companyRegNumber }));

//     dispatch(companySelectedAction(companyId, companyName, companyRegNumber));
//     history.push(`/${companyId}/dashboard`);
//   };
// }

// export function setPreSelectedCompany(companyId, companyName) {
//   return (dispatch) => {
//     dispatch(companySelectedAction(companyId, companyName));
//   };
// }

// export function createNewCompany(history) {
//   return (dispatch) => {
//     dispatch(createNewCompanyAction());
//     history.push('/create-company');
//   };
// }

// export function loginFailedAction(data) {
//   return {
//     type: LOGIN_FAILED_ACTION,
//     payload: data,
//   };
// }

// export function loginConfirmedAction(data) {
//   console.log("Login confirmed action: ", data);
//   return {
//     type: LOGIN_CONFIRMED_ACTION,
//     payload: {
//       data
//     },
//   };
// }

// export function confirmedSignupAction(payload) {
//   return {
//     type: SIGNUP_CONFIRMED_ACTION,
//     payload: {
//       data: payload,
//     },
//   };
// }

// export function signupFailedAction(message) {
//   return {
//     type: SIGNUP_FAILED_ACTION,
//     payload: message,
//   };
// }

// export function loadingToggleAction(status) {
//   return {
//     type: LOADING_TOGGLE_ACTION,
//     payload: status,
//   };
// }

// export function companySelectedAction(companyId, companyName, companyRegNumber) {
//   return {
//     type: COMPANY_SELECTED_ACTION,
//     payload: { companyId, companyName, companyRegNumber },
//   };
// }

// export function createNewCompanyAction(newCompany) {
//   return {
//     type: CREATE_NEW_COMPANY_ACTION,
//     payload: newCompany,
//   };
// }
